import React, { useMemo } from 'react';
import { PinningVolumeAnalysis, ConfluenceDetail } from '@shared/types';
import './PinningVolumeInfoOverlay.css';

interface PinningVolumeInfoOverlayProps {
  analysis: PinningVolumeAnalysis | null;
  isLoading?: boolean;
}

const PinningVolumeInfoOverlay: React.FC<PinningVolumeInfoOverlayProps> = React.memo(({ analysis, isLoading }) => {
  // Memoize display values
  const displayData = useMemo(() => {
    if (!analysis) {
      return null;
    }

    // Volume display
    const volumeRatioStr = analysis.volumeRatio.toFixed(1);
    const volumeMultiplierStr = analysis.volumeMultiplier.toFixed(1);
    const minVolumeRatioStr = analysis.minVolumeRatioSetting.toFixed(1);
    const hasValidVolumeSpike = analysis.volumeSpikeSource !== null;
    // Icons: ⚡ for current spike, ⚡⁻¹ for prev-1, ⚡⁻² for prev-2, ○ for no valid spike
    const volumeSpikeIcon = analysis.volumeSpikeSource === 'current' ? '⚡'
      : analysis.volumeSpikeSource === 'prev-1' ? '⚡⁻¹'
      : analysis.volumeSpikeSource === 'prev-2' ? '⚡⁻²'
      : '○';

    // Wick display - icons: ▲ upper, ▼ lower
    const wickPctStr = analysis.dominantWickPct.toFixed(0);
    const wickDirIcon = analysis.wickDirection === 'upper' ? '▲' : analysis.wickDirection === 'lower' ? '▼' : '–';

    // Prior move display - show calculation: currOpen - priorOpen = move
    const moveArrow = analysis.priorMoveDirection === 'up' ? '↑' : analysis.priorMoveDirection === 'down' ? '↓' : '–';
    const movePtsStr = Math.abs(analysis.priorMovePoints).toFixed(0);
    // Show the actual calculation for debugging
    const priorMoveCalc = analysis.candleOpen && analysis.priorCandleOpen
      ? `${analysis.candleOpen.toFixed(2)} - ${analysis.priorCandleOpen.toFixed(2)} = ${analysis.priorMovePoints > 0 ? '+' : ''}${analysis.priorMovePoints.toFixed(1)}`
      : null;

    // ATR display
    const atrStr = analysis.atrValue.toFixed(1);
    const atrLabelStr = analysis.atrLabel || '';

    // Verdict - compact
    const verdictIcon = analysis.isSignal ? '✓' : '✗';
    const verdictText = analysis.isSignal
      ? (analysis.signalType === 'bullish' ? 'LONG' : 'SHORT')
      : analysis.rejectionReasons.length > 0
        ? analysis.rejectionReasons[0]
        : 'NO';

    // Confluence data
    const hasConfluence = analysis.confluenceScore !== undefined && analysis.confluenceScore > 0;
    const confluenceScore = analysis.confluenceScore ?? 0;
    const confluenceDetails = analysis.confluenceDetails ?? [];
    const confluenceGroup = analysis.confluenceGroup ?? null;

    return {
      volumeRatioStr,
      volumeMultiplierStr,
      minVolumeRatioStr,
      hasValidVolumeSpike,
      volumeSpikeIcon,
      wickPctStr,
      wickDirIcon,
      hasSignificantWick: analysis.hasSignificantWick,
      isWickAligned: analysis.isWickAligned,
      moveArrow,
      movePtsStr,
      priorMoveDirection: analysis.priorMoveDirection,
      priorMoveCalc,
      priorMoveLookback: analysis.priorMoveLookback,
      atrStr,
      atrLabelStr,
      passesAtrFilter: analysis.passesAtrFilter,
      isSignal: analysis.isSignal,
      signalType: analysis.signalType,
      verdictIcon,
      verdictText,
      rejectionCount: analysis.rejectionReasons.length,
      hasConfluence,
      confluenceScore,
      confluenceDetails,
      confluenceGroup,
    };
  }, [analysis]);

  if (isLoading) {
    return (
      <div className="pinning-volume-info-overlay">
        <div className="pinning-volume-info-content">
          <span className="pinning-volume-info-loading">...</span>
        </div>
      </div>
    );
  }

  if (!analysis || !displayData) {
    return (
      <div className="pinning-volume-info-overlay">
        <div className="pinning-volume-info-content">
          <span className="pinning-volume-info-icon">📊</span>
          <span className="pinning-volume-info-value neutral">–</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pinning-volume-info-overlay">
      <div className="pinning-volume-info-content">
        {/* Volume Section: Vol 4.7x @2.1σ ⚡ */}
        <div className="pinning-volume-info-item">
          <span className="pinning-volume-info-label">Vol</span>
          <span className={`pinning-volume-info-value ${displayData.hasValidVolumeSpike ? 'pass' : 'neutral'}`}>
            {displayData.volumeRatioStr}x
          </span>
          <span className="pinning-volume-info-multiplier" title="Std Dev Multiplier">
            @{displayData.volumeMultiplierStr}σ
          </span>
          <span className="pinning-volume-info-threshold" title="Min Required">
            /{displayData.minVolumeRatioStr}
          </span>
          {displayData.hasValidVolumeSpike && (
            <span className="pinning-volume-info-spike-icon pass" title="Volume Spike">{displayData.volumeSpikeIcon}</span>
          )}
        </div>

        <div className="pinning-volume-info-separator">|</div>

        {/* Wick Section: Wick 42% ▼ ✓ */}
        <div className="pinning-volume-info-item">
          <span className="pinning-volume-info-label">Wick</span>
          <span className={`pinning-volume-info-value ${displayData.hasSignificantWick ? 'pass' : 'fail'}`}>
            {displayData.wickPctStr}%{displayData.wickDirIcon}
          </span>
          {displayData.hasSignificantWick && (
            <span className={`pinning-volume-info-align-icon ${displayData.isWickAligned ? 'pass' : 'fail'}`} title={displayData.isWickAligned ? 'Aligned' : 'Not Aligned'}>
              {displayData.isWickAligned ? '✓' : '✗'}
            </span>
          )}
        </div>

        <div className="pinning-volume-info-separator">|</div>

        {/* Prior Move Section: 15↓ with calculation tooltip */}
        <div className="pinning-volume-info-item">
          <span className={`pinning-volume-info-value ${
            displayData.priorMoveDirection === 'down' ? 'bullish' :
            displayData.priorMoveDirection === 'up' ? 'bearish' : 'neutral'
          }`} title={displayData.priorMoveCalc ? `Prior Move (${displayData.priorMoveLookback} bars): ${displayData.priorMoveCalc}` : `Prior Move (${displayData.priorMoveLookback} bars)`}>
            {displayData.movePtsStr}{displayData.moveArrow}
          </span>
        </div>

        <div className="pinning-volume-info-separator">|</div>

        {/* ATR Section: ATR 6.9 Low */}
        <div className="pinning-volume-info-item">
          <span className="pinning-volume-info-label">ATR</span>
          <span className={`pinning-volume-info-value ${displayData.passesAtrFilter ? 'pass' : 'warning'}`}>
            {displayData.atrStr}
          </span>
          {displayData.atrLabelStr && (
            <span className="pinning-volume-info-atr-label" title="ATR Range">{displayData.atrLabelStr}</span>
          )}
        </div>

        {/* Verdict Section: ✓ LONG or ✗ reason - always on new line */}
        <div className="pinning-volume-info-item verdict">
          <span className={`pinning-volume-info-verdict ${displayData.isSignal ? 'signal' : 'no-signal'}`}>
            {displayData.verdictIcon} {displayData.verdictText}
          </span>
        </div>

        {/* Confluence Section - only show if confluence data exists */}
        {(displayData.hasConfluence || displayData.confluenceDetails.length > 0) && (
          <>
            <div className="pinning-volume-info-separator full">|</div>
            <div className="pinning-volume-info-item confluence">
              <span className="pinning-volume-info-label">Confluence</span>
              <span className={`pinning-volume-info-confluence-score ${
                displayData.confluenceScore >= 50 ? 'high' :
                displayData.confluenceScore >= 30 ? 'medium' : 'low'
              }`}>
                {displayData.confluenceScore} pts
              </span>
              {displayData.confluenceGroup && (
                <span className="pinning-volume-info-confluence-group">
                  ({displayData.confluenceGroup})
                </span>
              )}
            </div>
            {displayData.confluenceDetails.map((detail: ConfluenceDetail, idx: number) => (
              <div key={idx} className="pinning-volume-info-item confluence-detail">
                <span className="pinning-volume-info-confluence-symbol">
                  {detail.symbol.replace('.CME', '')}
                </span>
                <span className="pinning-volume-info-confluence-bar">
                  {detail.barOffset === 0 ? 'same' : 'prev'}
                </span>
                {detail.volumeRatio !== null ? (
                  <>
                    <span className="pinning-volume-info-confluence-ratio">
                      {detail.volumeRatio.toFixed(1)}x
                    </span>
                    <span className={`pinning-volume-info-confluence-dir ${
                      detail.directionMatch ? 'match' : 'mismatch'
                    }`}>
                      {detail.direction === 'bullish' ? '↑' : detail.direction === 'bearish' ? '↓' : '–'}
                    </span>
                  </>
                ) : (
                  <span className="pinning-volume-info-confluence-none">no spike</span>
                )}
                <span className={`pinning-volume-info-confluence-pts ${detail.points > 0 ? 'positive' : ''}`}>
                  {detail.points > 0 ? `+${detail.points}` : '0'}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
});

export default PinningVolumeInfoOverlay;
